import React, { useState, useEffect, useRef } from 'react';
import { 
  HelpCircle, X, Search, ChevronRight, ChevronLeft, 
  MessageSquare, Send, Bot, User, Loader2, 
  ThumbsUp, ThumbsDown, Home
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HelpCenter = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState('home'); // 'home', 'category', 'search', 'chat'
  const [faqCategories, setFaqCategories] = useState({});
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [expandedQuestion, setExpandedQuestion] = useState(null);
  
  // Chat state
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  
  const chatEndRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Load FAQ categories
  useEffect(() => {
    const loadFaq = async () => {
      try {
        const response = await axios.get(`${API}/help/faq`);
        setFaqCategories(response.data);
      } catch (err) {
        console.error('Error loading FAQ:', err);
      }
    };
    loadFaq();
  }, []);

  // Auto-scroll chat
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages]);

  // Search with debounce
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchQuery.length >= 2) {
      setIsSearching(true);
      searchTimeoutRef.current = setTimeout(async () => {
        try {
          const response = await axios.get(`${API}/help/search`, {
            params: { q: searchQuery }
          });
          setSearchResults(response.data.results);
          setView('search');
        } catch (err) {
          console.error('Search error:', err);
        } finally {
          setIsSearching(false);
        }
      }, 300);
    } else {
      setSearchResults([]);
      if (view === 'search') setView('home');
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  // Send chat message
  const sendChatMessage = async () => {
    if (!chatInput.trim() || isChatLoading) return;

    const userMessage = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsChatLoading(true);

    try {
      const response = await axios.post(`${API}/help/chat`, {
        message: userMessage,
        session_id: sessionId,
        history: chatMessages.slice(-10)
      });

      setSessionId(response.data.session_id);
      setChatMessages(prev => [...prev, { 
        role: 'assistant', 
        content: response.data.response,
        source: response.data.source,
        faq_match: response.data.faq_match
      }]);
    } catch (err) {
      console.error('Chat error:', err);
      setChatMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Désolé, une erreur s\'est produite. Veuillez réessayer.',
        isError: true
      }]);
    } finally {
      setIsChatLoading(false);
    }
  };

  // Start chat with initial question
  const startChatWithQuestion = (question) => {
    setChatInput(question);
    setView('chat');
    setChatMessages([]);
  };

  const renderHome = () => (
    <div className="flex flex-col h-full">
      {/* Search bar */}
      <div className="p-4 border-b border-gray-100">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Rechercher dans l'aide..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00D3A8] focus:border-transparent text-sm"
          />
          {isSearching && (
            <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 animate-spin" size={18} />
          )}
        </div>
      </div>

      {/* Categories */}
      <div className="flex-1 overflow-y-auto p-4">
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
          Catégories d'aide
        </h3>
        <div className="space-y-2">
          {Object.entries(faqCategories).map(([key, category]) => (
            <button
              key={key}
              onClick={() => {
                setSelectedCategory({ key, ...category });
                setView('category');
              }}
              className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors text-left"
            >
              <div className="flex items-center space-x-3">
                <span className="text-xl">{category.icon}</span>
                <span className="font-medium text-gray-700">{category.title}</span>
              </div>
              <ChevronRight className="text-gray-400" size={18} />
            </button>
          ))}
        </div>

        {/* AI Chat CTA */}
        <div className="mt-6 p-4 bg-gradient-to-r from-[#00D3A8]/10 to-[#0099CC]/10 rounded-xl border border-[#00D3A8]/20">
          <div className="flex items-start space-x-3">
            <div className="w-10 h-10 bg-[#00D3A8] rounded-full flex items-center justify-center flex-shrink-0">
              <Bot className="text-white" size={20} />
            </div>
            <div>
              <h4 className="font-semibold text-gray-800">Besoin d'aide personnalisée ?</h4>
              <p className="text-sm text-gray-600 mt-1">
                Notre assistant IA peut répondre à vos questions spécifiques.
              </p>
              <button
                onClick={() => setView('chat')}
                className="mt-3 px-4 py-2 bg-[#00D3A8] text-white text-sm font-medium rounded-lg hover:bg-[#00C7A1] transition-colors"
              >
                Démarrer une conversation
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderCategory = () => (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-gray-100 flex items-center space-x-3">
        <button
          onClick={() => setView('home')}
          className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ChevronLeft className="text-gray-600" size={20} />
        </button>
        <span className="text-xl">{selectedCategory?.icon}</span>
        <h3 className="font-semibold text-gray-800">{selectedCategory?.title}</h3>
      </div>

      {/* Questions */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="space-y-2">
          {selectedCategory?.questions?.map((qa, index) => (
            <div key={index} className="border border-gray-100 rounded-lg overflow-hidden">
              <button
                onClick={() => setExpandedQuestion(expandedQuestion === index ? null : index)}
                className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
              >
                <span className="font-medium text-gray-700 pr-4">{qa.q}</span>
                <ChevronRight 
                  className={`text-gray-400 flex-shrink-0 transition-transform ${expandedQuestion === index ? 'rotate-90' : ''}`} 
                  size={18} 
                />
              </button>
              {expandedQuestion === index && (
                <div className="px-4 pb-4 pt-0">
                  <p className="text-gray-600 text-sm whitespace-pre-line bg-gray-50 p-3 rounded-lg">
                    {qa.a}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Still need help? */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500 mb-2">Vous n'avez pas trouvé votre réponse ?</p>
          <button
            onClick={() => setView('chat')}
            className="text-[#00D3A8] text-sm font-medium hover:underline"
          >
            Poser une question à l'assistant →
          </button>
        </div>
      </div>
    </div>
  );

  const renderSearch = () => (
    <div className="flex flex-col h-full">
      {/* Search bar */}
      <div className="p-4 border-b border-gray-100">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Rechercher dans l'aide..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00D3A8] focus:border-transparent text-sm"
            autoFocus
          />
          {searchQuery && (
            <button
              onClick={() => {
                setSearchQuery('');
                setView('home');
              }}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X size={18} />
            </button>
          )}
        </div>
      </div>

      {/* Results */}
      <div className="flex-1 overflow-y-auto p-4">
        {searchResults.length > 0 ? (
          <>
            <p className="text-sm text-gray-500 mb-3">
              {searchResults.length} résultat{searchResults.length > 1 ? 's' : ''} pour "{searchQuery}"
            </p>
            <div className="space-y-3">
              {searchResults.map((result, index) => (
                <div key={index} className="border border-gray-100 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <span>{result.category_icon}</span>
                    <span className="text-xs text-gray-500">{result.category}</span>
                  </div>
                  <h4 className="font-medium text-gray-800 mb-2">{result.question}</h4>
                  <p className="text-sm text-gray-600">{result.answer}</p>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500 mb-4">Aucun résultat trouvé</p>
            <button
              onClick={() => startChatWithQuestion(searchQuery)}
              className="text-[#00D3A8] font-medium hover:underline"
            >
              Poser cette question à l'assistant →
            </button>
          </div>
        )}
      </div>
    </div>
  );

  const renderChat = () => (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-gray-100 flex items-center space-x-3">
        <button
          onClick={() => setView('home')}
          className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <Home className="text-gray-600" size={20} />
        </button>
        <div className="w-8 h-8 bg-[#00D3A8] rounded-full flex items-center justify-center">
          <Bot className="text-white" size={16} />
        </div>
        <div>
          <h3 className="font-semibold text-gray-800 text-sm">Assistant SyndicAppli</h3>
          <p className="text-xs text-gray-500">En ligne</p>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {chatMessages.length === 0 && (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-[#00D3A8]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Bot className="text-[#00D3A8]" size={32} />
            </div>
            <h4 className="font-semibold text-gray-800 mb-2">Comment puis-je vous aider ?</h4>
            <p className="text-sm text-gray-500 mb-4">
              Posez-moi vos questions sur SyndicAppli
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              {['Comment voter en AG ?', 'Déclarer un incident', 'Mot de passe oublié'].map((q, i) => (
                <button
                  key={i}
                  onClick={() => {
                    setChatInput(q);
                    setTimeout(() => sendChatMessage(), 100);
                  }}
                  className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-full text-sm text-gray-700 transition-colors"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {chatMessages.map((msg, index) => (
          <div
            key={index}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div className={`max-w-[85%] ${msg.role === 'user' ? 'order-2' : 'order-1'}`}>
              {msg.role === 'assistant' && (
                <div className="flex items-center space-x-2 mb-1">
                  <div className="w-6 h-6 bg-[#00D3A8] rounded-full flex items-center justify-center">
                    <Bot className="text-white" size={12} />
                  </div>
                  <span className="text-xs text-gray-500">
                    {msg.source === 'faq' ? 'FAQ' : 'Assistant IA'}
                  </span>
                </div>
              )}
              <div
                className={`px-4 py-3 rounded-2xl ${
                  msg.role === 'user'
                    ? 'bg-[#00D3A8] text-white rounded-br-md'
                    : msg.isError
                    ? 'bg-red-50 text-red-700 border border-red-100'
                    : 'bg-gray-100 text-gray-800 rounded-bl-md'
                }`}
              >
                <p className="text-sm whitespace-pre-line">{msg.content}</p>
              </div>
            </div>
          </div>
        ))}

        {isChatLoading && (
          <div className="flex justify-start">
            <div className="bg-gray-100 px-4 py-3 rounded-2xl rounded-bl-md">
              <div className="flex items-center space-x-2">
                <Loader2 className="animate-spin text-gray-400" size={16} />
                <span className="text-sm text-gray-500">Réflexion en cours...</span>
              </div>
            </div>
          </div>
        )}

        <div ref={chatEndRef} />
      </div>

      {/* Input */}
      <div className="p-4 border-t border-gray-100">
        <div className="flex space-x-2">
          <input
            type="text"
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && sendChatMessage()}
            placeholder="Posez votre question..."
            className="flex-1 px-4 py-2.5 border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-[#00D3A8] focus:border-transparent text-sm"
            disabled={isChatLoading}
          />
          <button
            onClick={sendChatMessage}
            disabled={!chatInput.trim() || isChatLoading}
            className="p-2.5 bg-[#00D3A8] text-white rounded-full hover:bg-[#00C7A1] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send size={18} />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Floating Help Button */}
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-6 right-6 z-40 w-14 h-14 bg-[#00D3A8] hover:bg-[#00C7A1] text-white rounded-full shadow-lg hover:shadow-xl transition-all flex items-center justify-center ${isOpen ? 'scale-0' : 'scale-100'}`}
        data-testid="help-center-btn"
      >
        <HelpCircle size={28} />
      </button>

      {/* Help Center Panel */}
      <div
        className={`fixed bottom-6 right-6 z-50 w-[380px] h-[580px] bg-white rounded-2xl shadow-2xl transition-all duration-300 overflow-hidden ${
          isOpen ? 'scale-100 opacity-100' : 'scale-0 opacity-0 pointer-events-none'
        }`}
        style={{ transformOrigin: 'bottom right' }}
      >
        {/* Panel Header */}
        <div className="bg-gradient-to-r from-[#00D3A8] to-[#0099CC] p-4 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <HelpCircle size={24} />
              </div>
              <div>
                <h2 className="font-bold text-lg">Besoin d'aide ?</h2>
                <p className="text-white/80 text-sm">FAQ & Assistant IA</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="p-2 hover:bg-white/20 rounded-full transition-colors"
              data-testid="help-center-close"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Panel Content */}
        <div className="h-[calc(100%-88px)]">
          {view === 'home' && renderHome()}
          {view === 'category' && renderCategory()}
          {view === 'search' && renderSearch()}
          {view === 'chat' && renderChat()}
        </div>
      </div>
    </>
  );
};

export default HelpCenter;
